import { COST_REGEX, HEAVY_METAL_REGEX } from 'constants/regex';
import { ValidatorConfig } from 'csv-file-validator';

type Value = string | number | boolean;

// Common validation methods to reduce code duplication
const createValidators = {
  // Number validation for optional fields
  optionalNumber: () => ({
    validate: (value: Value): boolean => {
      if (!value) return true; // Allow empty for optional fields
      return !isNaN(Number(value));
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} must be a number in column ${columnNumber} (row ${rowNumber})`,
  }),

  // Number validation for required fields
  requiredNumber: () => ({
    validate: (value: Value): boolean => {
      if (!value) return false;
      return !isNaN(Number(value));
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} must be a number in column ${columnNumber} (row ${rowNumber})`,
  }),

  // Required field validation
  required: () => ({
    requiredError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} is missing in column ${columnNumber} (row ${rowNumber})`,
  }),

  // cost validation
  costValidation: () => ({
    validate: (value: Value): boolean => {
      if (!value) return false; // Allow empty for optional fields
      return COST_REGEX.test(`${value}`);
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} is missing or invalid value in column ${columnNumber} (row ${rowNumber}). \n Allowed formats: ($00.00, 00.00)`,
  }),

  // heavy metal validation
  heavyMetalValidation: () => ({
    validate: (value: Value): boolean => {
      if (value) {
        return HEAVY_METAL_REGEX.test(`${value}`);
      }
      return true;
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} is missing or invalid value in column ${columnNumber} (row ${rowNumber}).\n Format should be <, >, <=, >=, or a number.`,
  }),

  // Enum validation for optional fields
  optionalEnum: (allowedValues: string[]) => ({
    validate: (value: Value): boolean => {
      if (!value) return true; // Allow empty for optional fields
      return allowedValues.includes(value.toString());
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} must be one of: ${allowedValues.join(
        ', ',
      )} in column ${columnNumber} (row ${rowNumber})`,
  }),

  // Enum validation for required fields
  requiredEnum: (allowedValues: string[]) => ({
    validate: (value: Value): boolean => {
      if (!value) return false;
      return allowedValues.includes(value.toString());
    },
    validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
      `${headerName} must be one of: ${allowedValues.join(
        ', ',
      )} in column ${columnNumber} (row ${rowNumber})`,
  }),
};

const ALLERGENS = [
  'celery',
  'custaceans',
  'egg',
  'fish',
  'gluten',
  'lupin',
  'milk',
  'molluscs',
  'mustard',
  'peanut',
  'sesameSeeds',
  'shellfish',
  'soy',
  'sulphites',
  'treeNuts',
  'wheat',
];

const CERTIFICATIONS = ['vegan', 'non_gmo', 'kosher', 'gluten_Free'];

const NUTRIENTS = [
  'organic',
  'moisture',
  'calories',
  'protein',
  'carbohydrates',
  'dietaryFiber',
  'totalSugars',
  'addedSugar',
  'totalFat',
  'saturatedFat',
  'monounsaturatedFat',
  'polyunsaturatedFat',
  'transFat',
  'cholesterol',
  'sodium',
  'vitaminA',
  'vitaminC',
  'vitaminD',
  'vitaminE',
  'vitamink',
  'vitaminK2',
  'thiamin',
  'riboflavin',
  'niacin',
  'vitaminB6',
  'folicAcid',
  'vitaminB12',
  'biotin',
  'pantothenicAcid',
  'choline',
  'calcium',
  'iron',
  'phosphorus',
  'iodine',
  'magnesium',
  'zinc',
  'selenium',
  'copper',
  'manganese',
  'chromium',
  'molybdenum',
  'chloride',
  'potassium',
  'boron',
];

const HEAVY_METALS = ['arsenic', 'cadmium', 'lead', 'mercury'];
const PRODUCT_TYPES = ['C', 'T', 'P', 'G', 'L'];

export const csvConfig: ValidatorConfig = {
  headers: [
    // Basic Information
    {
      name: 'category',
      inputName: 'category',
      required: true,
      ...createValidators.required(),
    },
    {
      name: 'subCategory',
      inputName: 'subCategory',
      optional: true,
    },
    {
      name: 'name',
      inputName: 'name',
      required: true,
      ...createValidators.required(),
    },
    { name: 'scientificName', inputName: 'scientificName', optional: true },
    { name: 'latinName', inputName: 'latinName', optional: true },
    { name: 'tradeMarkName', inputName: 'tradeMarkName', optional: true },
    {
      name: 'ingredientProductTypes',
      inputName: 'ingredientProductTypes',
      required: true,
      validate: (value: string | number | boolean): boolean => {
        if (!value) return false;
        const types = value
          .toString()
          .split(',')
          .map((t: string) => t.trim());
        return types.every((t: string) => PRODUCT_TYPES.includes(t));
      },
      validateError: (headerName: string, rowNumber: number, columnNumber: number) =>
        `${headerName} must be one of: ${PRODUCT_TYPES.join(', ')} (comma-separated) in column ${columnNumber} (row ${rowNumber})`,
    },

    // Quantitative Data
    {
      name: 'amount',
      inputName: 'amount',
      required: true,
      ...createValidators.required(),
    },
    {
      name: 'potency',
      inputName: 'potency',
      optional: true,
      ...createValidators.requiredNumber(),
    },
    {
      name: 'cost',
      inputName: 'cost',
      optional: true,
      ...createValidators.costValidation(),
    },
    {
      name: 'suggestedOverage',
      inputName: 'suggestedOverage',
      optional: true,
      ...createValidators.optionalNumber(),
    },
    {
      name: 'gummyOverage',
      inputName: 'gummyOverage',
      optional: true,
      ...createValidators.optionalNumber(),
    },

    // Manufacturer Info
    { name: 'primary', inputName: 'primary', optional: true },
    { name: 'secondary', inputName: 'secondary', optional: true },
    { name: 'tertiary', inputName: 'tertiary', optional: true },
    { name: 'manufacturingCountry', inputName: 'manufacturingCountry', optional: true },
    { name: 'originCountry', inputName: 'originCountry', optional: true },

    // Physical Properties
    {
      name: 'shelfLife',
      inputName: 'shelfLife',
      optional: true,
      ...createValidators.optionalNumber(),
    },
    {
      name: 'density',
      inputName: 'density',
      optional: true,
      ...createValidators.optionalNumber(),
    },
    {
      name: 'avergageParticleSize',
      inputName: 'avergageParticleSize',
      optional: true,
      ...createValidators.optionalNumber(),
    },
    {
      name: 'lossOnDrying',
      inputName: 'lossOnDrying',
      optional: true,
      ...createValidators.optionalNumber(),
    },

    // Heavy Metals
    ...HEAVY_METALS.map((heavyMetal) => ({
      name: heavyMetal,
      inputName: heavyMetal,
      optional: true,
      ...createValidators.heavyMetalValidation(),
    })),

    // Allergens (all should be F/T)

    ...ALLERGENS.map((allergen) => ({
      name: allergen,
      inputName: allergen,
      optional: true,
      ...createValidators.optionalEnum(['F', 'T']),
    })),

    // Certifications
    ...CERTIFICATIONS.map((certification) => ({
      name: certification,
      inputName: certification,
      optional: true,
      ...createValidators.optionalEnum(['Y', 'N']),
    })),

    // Nutritional Information (all optional numbers)
    ...NUTRIENTS.map((nutrient) => ({
      name: nutrient,
      inputName: nutrient,
      optional: true,
      ...createValidators.optionalNumber(),
    })),
  ],
  isHeaderNameOptional: false,
  parserConfig: {
    skipEmptyLines: false,
    trim: true,
  },
  isColumnIndexAlphabetic: false,
};
